import { useState, useEffect, useRef } from 'react';
import { api } from '../context/AuthContext';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const tallerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const repIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const gruaIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

// Haversine distance in km
function distKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, 13); }, [target]);
  return null;
}

export default function Talleres() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [radius, setRadius] = useState(10); // km
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);

  useEffect(() => {
    api.get('/shops').then(({ data }) => setShops(data)).catch(() => setShops([])).finally(() => setLoading(false));
  }, []);

  const locate = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const latlng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserPos(latlng);
        setFlyTarget(latlng);
        setLocating(false);
      },
      () => { alert('No se pudo obtener tu ubicación'); setLocating(false); }
    );
  };

  const filtered = shops.filter(s => {
    if (typeFilter !== 'ALL' && s.shop_type !== typeFilter) return false;
    if (userPos) return distKm(userPos.lat, userPos.lng, s.shop_lat, s.shop_lng) <= radius;
    return true;
  });

  const center = userPos || { lat: 9.9281, lng: -84.0907 };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 md:top-32 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">🔧 Talleres y Repuesteras</h1>
            <p className="text-sm text-gray-500">{filtered.length} encontrados</p>
          </div>

          {/* Type filter */}
          <div className="flex gap-2">
            {[['ALL','Todos'],['TALLER','🔧 Talleres'],['REPUESTERA','🛞 Repuesteras'],['GRUA','🚛 Grúas']].map(([v,l]) => (
              <button key={v} onClick={() => setTypeFilter(v)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${typeFilter === v ? 'bg-black text-white border-black' : 'border-gray-300 hover:border-gray-500'}`}>
                {l}
              </button>
            ))}
          </div>

          {/* Radius (only when user located) */}
          {userPos && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Radio:</span>
              <select value={radius} onChange={e => setRadius(Number(e.target.value))}
                className="border rounded-lg px-2 py-1.5 text-sm">
                {[5,10,20,50,100].map(r => <option key={r} value={r}>{r} km</option>)}
              </select>
            </div>
          )}

          {/* Locate me */}
          <button onClick={locate} disabled={locating}
            className="flex items-center gap-2 px-4 py-2 bg-primary rounded-full text-sm font-semibold disabled:opacity-50">
            📍 {locating ? 'Localizando...' : userPos ? 'Mi ubicación' : 'Buscar cerca de mí'}
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-180px)]">
        {/* Sidebar list */}
        <div className="hidden md:flex flex-col w-72 shrink-0 border-r overflow-y-auto bg-white">
          {loading ? (
            <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No hay resultados</p>
          ) : filtered.map(s => (
            <div key={s.id} className="p-4 border-b hover:bg-gray-50">
              <a href={`/shops/${s.id}`} className="flex items-center gap-3 cursor-pointer">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${s.shop_type === 'TALLER' ? 'bg-red-500' : s.shop_type === 'GRUA' ? 'bg-orange-500' : 'bg-blue-500'}`}>
                  {s.shop_type === 'TALLER' ? '🔧' : s.shop_type === 'GRUA' ? '🚛' : '🛞'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.shop_type === 'TALLER' ? 'Taller' : s.shop_type === 'GRUA' ? 'Grúa' : 'Repuestera'}</p>
                  {s.shop_address && <p className="text-xs text-gray-400 truncate">{s.shop_address}</p>}
                  {userPos && (
                    <p className="text-xs text-primary font-medium">
                      {distKm(userPos.lat, userPos.lng, s.shop_lat, s.shop_lng).toFixed(1)} km
                    </p>
                  )}
                </div>
              </a>
            </div>
          ))}
        </div>

        {/* Map */}
        <div className="flex-1">
          <MapContainer
            center={center}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
            <FlyTo target={flyTarget} />

            {/* User position + radius circle */}
            {userPos && (
              <>
                <Marker position={userPos} icon={userIcon}>
                  <Popup>📍 Tu ubicación</Popup>
                </Marker>
                <Circle center={userPos} radius={radius * 1000}
                  pathOptions={{ color: '#f59e0b', fillColor: '#fef3c7', fillOpacity: 0.2 }} />
              </>
            )}

            {/* Shop pins */}
            {filtered.map(s => (
              <Marker key={s.id}
                position={{ lat: s.shop_lat, lng: s.shop_lng }}
                icon={s.shop_type === 'TALLER' ? tallerIcon : s.shop_type === 'GRUA' ? gruaIcon : repIcon}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <p className="font-bold text-base">{s.name}</p>
                    <p className="text-sm text-gray-500 mb-2">{s.shop_type === 'TALLER' ? '🔧 Taller' : s.shop_type === 'GRUA' ? '🚛 Grúa' : '🛞 Repuestera'}</p>
                    {s.shop_address && <p className="text-xs text-gray-600 mb-2">📍 {s.shop_address}</p>}
                    {userPos && (
                      <p className="text-xs text-primary font-semibold mb-2">
                        {distKm(userPos.lat, userPos.lng, s.shop_lat, s.shop_lng).toFixed(1)} km
                      </p>
                    )}
                    <a href={`/shops/${s.id}`} className="block mt-2 text-center bg-primary text-white text-xs px-3 py-2 rounded-lg hover:bg-primary-dark transition">
                      Ver perfil →
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Legend */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-lg px-4 py-2 flex gap-4 text-xs z-[1000]">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Taller</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> Repuestera</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-500 inline-block" /> Grúa</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Tú</span>
      </div>
    </div>
  );
}
